import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, ListSubheader, Menu, MenuItem, Typography } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { api } from '../../services/apiService';
import { isUpdateApiFailure } from '../../sagas/apiFailure';
import DailyLimitPanel from '../daily-limit/DailyLimitPanel';
import { allowanceLeftLine } from '../daily-limit/daily-limit';
import { REMINDER, unlockLine } from './first-quiz-commitment';
import {
  buildIcs, downloadIcs, googleCalendarUrl, practiceLink, reminderEventText, reminderPhrase, reminderStart,
} from './calendar';
import { FIRST_QUIZ_ACTIONS, trackFirstQuizCta } from './receipt';
import './first-quiz-commitment.scss';

const REMIND_SAVED = "You're set. We'll email you a reminder.";
const REMIND_FAILED = "That didn't save. Please try again.";
const ICS_SAVED = 'Open qlearn-practice.ics from your downloads to add the event.';
const ICS_FAILED = "This browser can't save calendar files. Try Google Calendar.";

// One "shown" per quiz per page load, however often the page re-renders or the
// student comes back to this result with the back button.
const shownQuizzes = new Set();
export const resetFirstQuizShownForTests = () => shownQuizzes.clear();

const currentOrigin = () => {
  try {
    return window.location.origin;
  } catch {
    return '';
  }
};

// "8:00 PM" never breaks between the time and AM/PM on a narrow phone.
const keepTimeTogether = (text) => text.replace(/(\d) ([AP]M)\b/g, '$1\u00a0$2');

/**
 * The first quiz's "come back tomorrow" block, right under the score. It takes
 * the place of the ordinary next step: "Start another quiz" while questions are
 * left today, and the daily-limit card's trial or plans button once they are
 * used up (that card's words would repeat this block's, so only its buttons are
 * kept, with its own analytics).
 *
 * No score or streak line: the score card sits right above, and nothing else in
 * the app keeps a streak yet (UX review, 2026-10-31).
 *
 * Props:
 * - examId: this quiz (the "shown" latch).
 * - info: today's allowance (resolveDailyLimit's result).
 * - examTypes: the quiz's exam types (the practice link: ABSITE's own entry
 *   when the quiz is ABSITE only).
 * - reminder: REMINDER.ON | OFF | HIDDEN (use-first-quiz.js).
 * - userId: for turning practice emails on.
 * - onStartNext, onSeePlans, onDismiss: the page's navigation and dismissal.
 * - dailyLimitShownRef: shared with the page's daily-limit card, so
 *   daily_limit_shown is logged once even if the student dismisses this block.
 * - now, timeZone: for tests.
 */
export default function FirstQuizCommitment({
  examId, info, examTypes, reminder = REMINDER.HIDDEN, userId,
  onStartNext, onSeePlans, onDismiss, dailyLimitShownRef, now, timeZone,
}) {
  // One clock per mount: the unlock line, the reminder time and the event agree.
  const clock = useMemo(() => now || new Date(), [now]);
  const [remind, setRemind] = useState('idle');
  const [calendarNote, setCalendarNote] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const remindTracked = useRef(false);
  const calendarTracked = useRef(false);
  const remindNoteRef = useRef(null);

  useEffect(() => {
    if (examId && !shownQuizzes.has(examId)) {
      shownQuizzes.add(examId);
      trackFirstQuizCta(FIRST_QUIZ_ACTIONS.SHOWN);
    }
  }, [examId]);

  // The confirmation takes the button's place; focus goes with it, so keyboard
  // and screen-reader users are not dropped at <body>.
  useEffect(() => {
    if (remind === 'saved') remindNoteRef.current?.focus({ preventScroll: true });
  }, [remind]);

  const left = Number.isInteger(info?.left) ? info.left : 0;
  const exam = Array.isArray(examTypes) && examTypes.length === 1 ? examTypes[0] : null;
  const calendar = useMemo(() => {
    const start = reminderStart(clock, { timeZone });
    const link = practiceLink({ origin: currentOrigin(), exam });
    const { title, description } = reminderEventText({ info, link });
    return { start, link, title, description, when: keepTimeTogether(reminderPhrase(start, clock, { timeZone })) };
  }, [clock, timeZone, exam, info]);

  const onRemind = async () => {
    if (!remindTracked.current) {
      remindTracked.current = true;
      trackFirstQuizCta(FIRST_QUIZ_ACTIONS.REMIND_ME);
    }
    if (reminder === REMINDER.ON) {
      // Already on (the default for new accounts): nothing to save.
      setRemind('saved');
      return;
    }
    setRemind('saving');
    let saved = false;
    try {
      const res = await api.setPreference({ userId, body: { emailPracticeQuestions: true } });
      saved = Boolean(res) && !isUpdateApiFailure(res);
    } catch {
      saved = false;
    }
    setRemind(saved ? 'saved' : 'failed');
  };

  const closeMenu = () => setMenuAnchor(null);
  // One "calendar" per view, whichever calendar (or both) the student picks.
  const trackCalendar = () => {
    if (!calendarTracked.current) {
      calendarTracked.current = true;
      trackFirstQuizCta(FIRST_QUIZ_ACTIONS.CALENDAR);
    }
  };
  const onGoogle = () => {
    trackCalendar();
    setCalendarNote(null);
    closeMenu();
  };
  const onIcs = () => {
    trackCalendar();
    const ics = buildIcs({ start: calendar.start, title: calendar.title, description: calendar.description,
      link: calendar.link, now: clock });
    setCalendarNote(downloadIcs(ics) ? { text: ICS_SAVED, role: 'status' } : { text: ICS_FAILED, role: 'alert' });
    closeMenu();
  };

  const onStart = () => {
    trackFirstQuizCta(FIRST_QUIZ_ACTIONS.START_NEXT);
    onStartNext();
  };
  const onClose = () => {
    trackFirstQuizCta(FIRST_QUIZ_ACTIONS.DISMISS);
    onDismiss();
  };

  const showRemind = reminder !== REMINDER.HIDDEN;

  return (
    <Box component="section" className="first-quiz-card" data-testid="first-quiz-commitment"
      aria-labelledby="first-quiz-title">
      <Box className="first-quiz-head">
        <Typography component="h2" id="first-quiz-title" className="first-quiz-title">
          First quiz done{'\u00a0'}✓
        </Typography>
        <IconButton aria-label="Dismiss first quiz message" className="first-quiz-dismiss" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </Box>
      {/* Bold while it is the news (today's questions used up); plain while the
          questions still left today are the thing to act on. */}
      <Typography variant="body2" data-testid="first-quiz-unlock"
        className={left > 0 ? 'first-quiz-unlock' : 'first-quiz-unlock first-quiz-unlock-lead'}>
        {keepTimeTogether(unlockLine(info, { now: clock, timeZone }))}
      </Typography>

      {/* The primary action leads: another quiz while questions are left, else
          the daily-limit card's "Try Pro free". The reminder and calendar
          buttons below are the secondary path. */}
      {left > 0 ? (
        <Box className="first-quiz-next">
          <Typography variant="body2" className="first-quiz-left">{allowanceLeftLine(info)}</Typography>
          <Box className="daily-limit-actions">
            <Button variant="contained" color="primary" onClick={onStart}>Start another quiz</Button>
          </Box>
        </Box>
      ) : (
        <Box className="first-quiz-next">
          <DailyLimitPanel variant="actions" surface="exam_result" info={info} onSeePlans={onSeePlans}
            shownRef={dailyLimitShownRef} now={clock} />
        </Box>
      )}
      <Box className="first-quiz-actions">
        {showRemind && remind === 'saved' && (
          <Typography variant="body2" className="first-quiz-note first-quiz-remind-done" role="status"
            tabIndex={-1} ref={remindNoteRef}>
            {REMIND_SAVED}
          </Typography>
        )}
        {showRemind && remind !== 'saved' && (
          <Button variant="outlined" color="primary" onClick={onRemind} disabled={remind === 'saving'}>
            {reminder === REMINDER.ON ? 'Email me a reminder' : 'Turn on practice emails'}
          </Button>
        )}
        <Button variant="outlined" color="primary" aria-haspopup="menu"
          aria-controls={menuAnchor ? 'first-quiz-calendar-menu' : undefined}
          aria-expanded={menuAnchor ? 'true' : undefined}
          onClick={(event) => setMenuAnchor(event.currentTarget)}>
          Add to calendar
        </Button>
      </Box>
      <Menu id="first-quiz-calendar-menu" className="first-quiz-menu" anchorEl={menuAnchor} keepMounted={false}
        open={Boolean(menuAnchor)} onClose={closeMenu} getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        MenuListProps={{
          // Named by its subheader, so the time is read out with the choices.
          'aria-labelledby': 'first-quiz-calendar-when',
          subheader: <ListSubheader id="first-quiz-calendar-when" disableSticky className="first-quiz-menu-when">
            {`Practice ${calendar.when}`}
          </ListSubheader>,
        }}>
        <MenuItem component="a" href={googleCalendarUrl(calendar)} target="_blank" rel="noopener noreferrer"
          aria-label="Google Calendar (opens in a new tab)" onClick={onGoogle}>
          Google Calendar
        </MenuItem>
        <MenuItem onClick={onIcs}>Apple or Outlook (.ics)</MenuItem>
      </Menu>
      {remind === 'failed' && (
        <Typography variant="body2" className="first-quiz-note" role="alert">{REMIND_FAILED}</Typography>
      )}
      {calendarNote && (
        <Typography variant="body2" className="first-quiz-note" role={calendarNote.role}>{calendarNote.text}</Typography>
      )}

    </Box>
  );
}
